import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { Warning } from '@wix/da-ds/pkg/Icons/Confirmations';
import { getUserWatchState } from './redux/selectors';
import { Url } from '@wix/da-url';
import { clickWatch } from './redux/actionCreators';
import { pushUnsubscribeModalByUsername } from '../Tier/redux/consumer/actions';
import { BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import { pushGlobalConfirmationModal } from '../Modals/redux/actionCreators';
function useWatchButtonText(isWatching, isWatchingBack, isSubscribed) {
    const { t  } = useTranslation();
    if (isSubscribed) {
        return t('watchButton.subscribed');
    }
    if (isWatching) {
        return t('watchButton.watching');
    }
    if (isWatchingBack) {
        return t('watchButton.watchBack');
    }
    return t('watchButton.watch');
}
export function useWatchButton(user, initialIsWatching = !!(user === null || user === void 0 ? void 0 : user.isWatching), isWatchingBack = false, disabled = false, onToggleWatchSuccess, allowUnsubscribe = false) {
    const isUnwatchConfirmFlagEnabled = useFlag('unwatch_confirm');
    const { isWatching , isLoading  } = useSelector((state)=>getUserWatchState(state, user, {
            isWatching: initialIsWatching
        }));
    const isSubscribed = !!(user && user.isSubscribed);
    const dispatch = useDispatch();
    const text = useWatchButtonText(isWatching, isWatchingBack, isSubscribed);
    const biData = useMemo(()=>{
        return BiData({
            evid: 380,
            is_watching: isWatching ? 0 : 1,
            friendid: user === null || user === void 0 ? void 0 : user.useridUuid,
            itemid: BiContextProvidedValue,
            typeid: BiContextProvidedValue,
            rank: BiContextProvidedValue,
            sectionid: BiContextProvidedValue,
            sectionname: BiContextProvidedValue,
            section_typeid: BiContextProvidedValue,
            sectiontype: BiContextProvidedValue,
            sectionrank: BiContextProvidedValue,
            sort_type: BiContextProvidedValue,
            reason_shown: BiContextProvidedValue,
            notification_type: BiContextProvidedValue
        });
    }, [
        isWatching,
        user
    ]);
    const onClick = useCallback(()=>{
        if (disabled || !user) {
            return;
        }
        if (isSubscribed) {
            if (allowUnsubscribe) {
                dispatch(pushUnsubscribeModalByUsername(user.username));
            } else {
                // redirect to subscriptions page
                window.location.href = Url.userSubscriptionsPath({
                    username: user.username
                });
            }
            return;
        }
        if (isWatching) {
            if (isUnwatchConfirmFlagEnabled) {
                dispatch(pushGlobalConfirmationModal({
                    titleTransKey: 'watchButton.unwatchConfirmModal.title',
                    textTransKey: 'watchButton.unwatchConfirmModal.description',
                    confirmBtnLabelTransKey: 'watchButton.unwatchConfirmModal.confirmButton.label',
                    cancelBtnLabelTransKey: 'watchButton.unwatchConfirmModal.cancelButton.label',
                    translationOptions: {
                        username: user.username
                    },
                    icon: /*#__PURE__*/ React.createElement(Warning, null),
                    onConfirm: ()=>{
                        dispatch(clickWatch(user, isWatching, onToggleWatchSuccess));
                    }
                }));
            } else {
                dispatch(clickWatch(user, isWatching, onToggleWatchSuccess));
            }
        } else {
            dispatch(clickWatch(user, isWatching, onToggleWatchSuccess));
        }
    }, [
        user,
        isWatching,
        isSubscribed,
        disabled,
        onToggleWatchSuccess,
        dispatch,
        allowUnsubscribe,
        isUnwatchConfirmFlagEnabled
    ]);
    return {
        text,
        onClick,
        isLoading,
        isWatching,
        isSubscribed,
        biData
    };
}
